import { Select, Box, Button } from "@sainsburys-tech/fable";
import Headerfile from "../common/Headerfile";
import React, { useState, useEffect } from "react";
import Footerfile from "../common/Footerfile";
import styles from "./PrinterDetails.module.css";
import { FiPlusCircle } from "@react-icons/all-files/fi/FiPlusCircle";
import { useNavigate } from "react-router-dom";
import { AiFillPrinter } from "@react-icons/all-files/ai/AiFillPrinter";
import { IconContext } from "react-icons";
import { useGetPostsQuery } from "../../api/apiConfig";
import { getLogisticsRole } from "../../utils/authToken";
import { USER_ROLE } from "../../constants/roles";
import Unauthorized from "../unauthorized/Unauthorized";

const PrinterDetails: React.FC = () => {
  const [image, setImage] = useState<string>("");
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null
  );
  const [printerId, setPrinterId] = useState<string | null>(null);
  const [printerNames, setPrinterNames] = useState<string[]>([]);
  const [printerDetails, setPrinterDetails] = useState<PrinterDetails | null>(
    null
  );

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
      loggedUserRole?.includes(USER_ROLE.READ_WRITE) ||
      loggedUserRole?.includes(USER_ROLE.READ_ONLY)
      ? false
      : true;

  interface Location {
    LocationId: string;
    Name: string;
  }

  interface PrinterDetails {
    IsRetired: string;
    Status: string;
    Configuration: {
      IP: string;
      Port: string;
      Type: string;
      IsShared: string;
      IsQueuingEnabled: string | undefined;
      IsPublished: string;
      LabelMediaType: string;
    };
    Identity: {
      SBPrinterId: string;
      Name: string;
      SerialNumber: string;
      Location: {
        Id: string;
        Name: string;
        WarehouseFloor: string;
        WarehouseLocality: string;
      };
      PrinterModel: {
        Id: string;
        Name: string;
      };
    };
    StatusOverride: {
      IsAllowed: boolean;
      PreventTill: null;
      StatusCode: string;
      StatusDescription: string;
      LastUpdate: Date;
    };
  }

  const {
    isLoading: isLocationLoading,
    isSuccess: isLocationSuccess,
    data: getAllLocations,
    refetch: fetchLocations
  } = useGetPostsQuery("locations/GetLocationFromIdentityToken");

  const {
    isLoading,
    isSuccess,
    data: getData,
    refetch
  } = useGetPostsQuery(
    selectedLocationId
      ? `printers/GetByLocationId?LocationId=${selectedLocationId}`
      : ""
  );
  const [printerModelId, setPrinterModelId] = useState<string>("");

  useEffect(() => {
    fetchLocations();
    refetch();
  }, []);

  useEffect(() => {
    if (isLocationSuccess && getAllLocations?.Model) {
      const validLocations = getAllLocations?.Model.filter(
        (location: any): location is Location =>
          location.LocationId &&
          location.Name &&
          location.Status
      );

      const sortedLocations: Location[] = validLocations.sort(
        (a: any, b: any) => a.Name.localeCompare(b.Name)
      );

      setLocation(sortedLocations);
    }
  }, [isLocationSuccess, getAllLocations]);

  useEffect(() => {
    if (isSuccess && getData?.Model) {
      const names = (getData.Model as PrinterDetails[])
        .map((printer) => printer.Identity.Name)
        .sort((a, b) => a.localeCompare(b));

      setPrinterNames(names);
      setImage("");
    }
  }, [isSuccess, getData]);

  useEffect(() => {
    if (printerId) {
      const selectedPrinter = (getData?.Model as PrinterDetails[])?.find(
        (printer) => printer.Identity.Name === printerId
      );
      setPrinterDetails(selectedPrinter || null);

      if (selectedPrinter) {
        setPrinterModelId(selectedPrinter?.Identity?.PrinterModel?.Id);
      }
    }
  }, [printerId]);

  interface PrinterModel {
    Id: string;
    Name: string;
    Image: string;
  }

  interface PrinterImage {
    StatusCode: number;
    Status: string;
    TotalRecords: number | null;
    Message: string | null;
    Model: PrinterModel;
    Value: any;
  }

  const isPrinterImage = (data: any): data is PrinterImage => {
    return data && data.Model && typeof data.Model.Image === "string";
  };

  const { data: printerImage } = useGetPostsQuery(
    printerModelId ? `printer-models/${printerModelId}` : ""
  );

  useEffect(() => {
    if (isPrinterImage(printerImage)) {
      setImage(printerImage.Model.Image);
    }
  }, [printerModelId, printerImage]);

  function toSentenceCase(str: string) {
    return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
  }

  const handleLocationChange = (e: string) => {
    setSelectedLocationId(e);
    setPrinterId("Select");
    setPrinterModelId("");
  };

  const handlePrinterChange = (e: string) => {
    if (e === "default") {
      setPrinterModelId("");
    }
    setPrinterId(e);
  };

  return (
    <>
      <Headerfile />
      {isDisabled ? (
        <Unauthorized />
      ) : (
        <>
          <div className={styles.buttonContainer}>
            <>
              <Button
                type="submit"
                className={`${styles.button}`}
                onClick={() => navigate("/addprintermodel")}
              >
                <IconContext.Provider value={{ size: "1.5em" }}>
                  <AiFillPrinter className="mx-1" /> Add Printer Model
                </IconContext.Provider>
              </Button>
              <Button
                type="submit"
                className={`${styles.button2} `}
                onClick={() => navigate("/addprinter")}
              >
                <IconContext.Provider value={{ size: "1.5em" }}>
                  <FiPlusCircle className="mx-1" /> Add Printer
                </IconContext.Provider>
              </Button>
            </>
          </div>
          <Box className={styles.container}>
            <div className={styles.formContainer}>
              <form>
                <Select
                  label="Location"
                  options={[
                    {
                      label: "Select Location",
                      value: "default"
                    },
                    ...(isLocationLoading
                      ? [
                        {
                          label: "Loading...",
                          value: "loading",
                          isDisabled: isLocationLoading
                        }
                      ]
                      : location?.map((loc) => ({
                        value: loc?.LocationId ?? "",
                        label: loc?.Name ?? ""
                      })) ?? [])
                  ]}
                  value={selectedLocationId || ""}
                  onChange={(e) => handleLocationChange(e)}
                />
                <br />
                <Select
                  label="Printer Name"
                  options={[
                    {
                      label: "Select Printer",
                      value: "default"
                    },
                    ...(isLoading
                      ? [
                        {
                          label: "Loading...",
                          value: "loading",
                          isDisabled: isLoading
                        }
                      ]
                      : printerNames?.map((name) => ({
                        value: name,
                        label: name
                      })))
                  ]}
                  value={printerId || ""}
                  onChange={(e) => handlePrinterChange(e)}
                />
                <div className="my-3">
                  <label>
                    <p className={styles.printerLabel}>Printer Details</p>
                  </label>
                  <table className={styles.table}>
                    <tbody>
                      <tr>
                        <td>Printer Name:</td>
                        <td>
                          {printerDetails?.Identity.Name
                            ? printerDetails?.Identity.Name
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>IP Address:</td>
                        <td>
                          {printerDetails?.Configuration.IP
                            ? printerDetails?.Configuration.IP
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>Port:</td>
                        <td>
                          {printerDetails?.Configuration.Port
                            ? printerDetails?.Configuration.Port
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>Location:</td>
                        <td>
                          {printerDetails?.Identity.Location.Name
                            ? printerDetails?.Identity.Location.Name
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>Printer Model:</td>
                        <td>
                          {printerDetails?.Identity.PrinterModel.Name
                            ? printerDetails?.Identity.PrinterModel.Name
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td> Printer Type:</td>
                        <td>
                          {printerDetails?.Configuration.Type
                            ? printerDetails?.Configuration.Type === "Z"
                              ? "ZPL"
                              : "Others"
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>Warehouse:</td>
                        <td>
                          {printerDetails?.Identity.Location.WarehouseFloor
                            ? printerDetails?.Identity.Location.WarehouseFloor
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td>Warehouse Locality:</td>
                        <td>
                          {printerDetails?.Identity.Location.WarehouseLocality
                            ? printerDetails?.Identity.Location
                              .WarehouseLocality
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>Status:</td>
                        <td>
                          {printerDetails?.Status
                            ? printerDetails?.Status === "A"
                              ? "Active"
                              : "InActive"
                            : "-"}
                        </td>
                      </tr>

                      <tr>
                        <td>Is Published:</td>
                        <td>
                          {printerDetails?.Configuration.IsPublished !== ""
                            ? printerDetails?.Configuration.IsPublished !==
                              undefined
                              ? toSentenceCase(
                                printerDetails?.Configuration.IsPublished.toString()
                              )
                              : "-"
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td>Is Retired:</td>
                        <td>
                          {printerDetails?.IsRetired !== ""
                            ? printerDetails?.IsRetired !== undefined
                              ? toSentenceCase(
                                printerDetails?.IsRetired.toString()
                              )
                              : "-"
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td>Is Shared:</td>
                        <td>
                          {printerDetails?.Configuration.IsShared !== ""
                            ? printerDetails?.Configuration.IsShared !==
                              undefined
                              ? toSentenceCase(
                                printerDetails?.Configuration.IsShared.toString()
                              )
                              : "-"
                            : "-"}
                        </td>
                      </tr>
                      <tr>
                        <td>Is Queuing Enabled:</td>
                        <td>
                          {toSentenceCase(printerDetails?.Configuration?.IsQueuingEnabled?.toString() ?? "-")}
                        </td>
                      </tr>
                      {
                        <tr>
                          <td>Label Media Type:</td>
                          <td>
                            {printerDetails?.Configuration?.LabelMediaType
                              ? printerDetails?.Configuration?.LabelMediaType
                              : "-"}
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </form>
            </div>
            <div className={styles.imageContainer}>
              {image && printerModelId ? (
                <img
                  alt={image}
                  src={`data:image/png;base64,${image}`}
                  className={styles.responsiveImage}
                />
              ) : (
                <h4>Not Available</h4>
              )}
            </div>
          </Box>
        </>
      )}
      <Footerfile />
    </>
  );
};
export default PrinterDetails;
